<template>
  <div class="metamask-connector">
    <div v-if="!isMetaMaskInstalled" class="metamask-not-installed">
      <b-alert show variant="warning">
        <i class="fa fa-exclamation-triangle mr-2"></i>
        MetaMask is not installed. Please install MetaMask to continue.
        <a href="https://metamask.io/download.html" target="_blank"
          >Download MetaMask</a
        >
      </b-alert>
    </div>
    <div v-else>
      <b-button
        v-if="!isConnected"
        variant="success"
        @click="connectMetaMask"
        size="sm"
        pill
        class="metamask-button"
        :disabled="connecting"
      >
        <img style="width: 20px" src="/metamask.svg" alt="Metamask icon" />
        {{ connecting ? "Connecting..." : "Connect MetaMask" }}
      </b-button>
      <div v-else class="connection-info">
        <div class="d-flex align-items-center">
          <div class="connected-status">
            <span class="status-dot"></span>
            <span class="ml-2">Connected</span>
            <b-button
              variant="outline-danger"
              size="sm"
              pill.
              style="width: 26px; height: 26px; font-size:12px;"
              class="ml-2 p-0"
              @click="disconnectMetaMask"
              title="Disconnect wallet"
              v-b-tooltip.hover
            >
              <i class="fa fa-chain-broken" aria-hidden="true"></i>
            </b-button>
          </div>
          <div class="address-display ml-3">
            {{ truncatedAddress }}
          </div>
          <div class="network-info ml-3" v-if="networkName">
            <b-badge class="px-4 w-auto" pill variant="success"
              ><span style="font-weight: 300">Network: </span> &nbsp;&nbsp;
              <strong>{{ networkName }}</strong></b-badge
            >
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import { ethers } from "ethers";
  import truncateString from "@/core/truncate";
  import networks from '@/data/networks.json'

  export default {
    name: "MetaMaskConnector",
    data() {
      return {
        provider: null,
        signer: null,
        account: null,
        chainId: null,
        networkName: null,
        isConnected: false,
        isMetaMaskInstalled: false,
        connecting: false,
        error: null,
      };
    },
    computed: {
      networksByChain(){
        const returnNetworks = []
        Object.values(networks).forEach(network => returnNetworks[network.chainId] = network.name)
        return returnNetworks
      },
      truncatedAddress() {
        return this.account ? truncateString(this.account) : "";
      },
    },
    mounted() {
      this.checkMetaMaskInstalled();
      this.checkStoredConnection();
      this.setupMetaMaskListeners();
    },
    beforeDestroy() {
      this.removeMetaMaskListeners();
    },
    methods: {
      truncateString,
      checkMetaMaskInstalled() {
        this.isMetaMaskInstalled =
          typeof window.ethereum !== "undefined" && window.ethereum.isMetaMask;
      },

      async checkStoredConnection() {
        const storedAccount = localStorage.getItem("metamask_account");
        if (storedAccount && this.isMetaMaskInstalled) {
          try {
            // Check if still authorized
            const accounts = await window.ethereum.request({
              method: "eth_accounts",
            });
            if (
              accounts.length > 0 &&
              accounts[0].toLowerCase() === storedAccount.toLowerCase()
            ) {
              await this.connectMetaMask(false); // Connect without user prompt
            } else {
              localStorage.removeItem("metamask_account");
            }
          } catch (error) {
            console.error("Failed to check stored connection:", error);
            localStorage.removeItem("metamask_account");
          }
        }
      },

      setupMetaMaskListeners() {
        if (window.ethereum) {
          window.ethereum.on("accountsChanged", this.handleAccountsChanged);
          window.ethereum.on("chainChanged", this.handleChainChanged);
          window.ethereum.on("disconnect", this.handleDisconnect);
        }
      },

      removeMetaMaskListeners() {
        if (window.ethereum) {
          window.ethereum.removeListener(
            "accountsChanged",
            this.handleAccountsChanged
          );
          window.ethereum.removeListener(
            "chainChanged",
            this.handleChainChanged
          );
          window.ethereum.removeListener("disconnect", this.handleDisconnect);
        }
      },

      async connectMetaMask(requestAccounts = true) {
        if (!this.isMetaMaskInstalled) return;

        this.connecting = true;
        this.error = null;

        try {
          // Initialize provider
          this.provider = new ethers.providers.Web3Provider(window.ethereum);

          // Get accounts
          let accounts;
          if (requestAccounts) {
            accounts = await window.ethereum.request({
              method: "eth_requestAccounts",
            });
          } else {
            accounts = await window.ethereum.request({
              method: "eth_accounts",
            });
          }

          if (accounts.length === 0) {
            throw new Error("No accounts authorized");
          }

          // Get network information
          const network = await this.provider.getNetwork();
          this.chainId = network.chainId;
          this.networkName = this.getNetworkName(network.chainId);

          // Set up account and signer
          this.account = accounts[0];
          this.signer = this.provider.getSigner();
          this.isConnected = true;

          // Store connection
          localStorage.setItem("metamask_account", this.account);

          // Emit connected event
          this.$emit("connected", {
            provider: this.provider,
            signer: this.signer,
            account: this.account,
            chainId: this.chainId,
            networkName: this.networkName,
          });
        } catch (error) {
          console.error("MetaMask connection error:", error);
          this.error = error.message || "Failed to connect to MetaMask";
          this.$emit("error", this.error);
        } finally {
          this.connecting = false;
        }
      },

      disconnectMetaMask() {
        this.provider = null;
        this.signer = null;
        this.account = null;
        this.chainId = null;
        this.networkName = null;
        this.isConnected = false;

        // Remove stored connection
        localStorage.removeItem("metamask_account");

        // Emit disconnected event
        this.$emit("disconnected");
      },

      async handleAccountsChanged(accounts) {
        if (accounts.length === 0) {
          // User disconnected all accounts
          this.disconnectMetaMask();
        } else if (this.account !== accounts[0]) {
          // Account changed, reconnect
          this.account = accounts[0];
          localStorage.setItem("metamask_account", this.account);

          if (this.provider) {
            this.signer = this.provider.getSigner();
            this.$emit("account-changed", {
              account: this.account,
              signer: this.signer,
            });
          } else {
            await this.connectMetaMask(false);
          }
        }
      },

      async handleChainChanged(chainIdHex) {
        const chainId = parseInt(chainIdHex, 16);

        if (this.chainId !== chainId) {
          this.chainId = chainId;
          this.networkName = this.getNetworkName(chainId);

          if (this.provider) {
            // Refresh provider and signer
            this.provider = new ethers.providers.Web3Provider(window.ethereum);
            this.signer = this.provider.getSigner();

            this.$emit("network-changed", {
              chainId: this.chainId,
              networkName: this.networkName,
              provider: this.provider,
              signer: this.signer,
            });
          }
        }
      },

      handleDisconnect(error) {
        console.log("MetaMask disconnected:", error);
        this.disconnectMetaMask();
      },

      getNetworkName(chainId) {
        return this.networksByChain[chainId] || `Chain ID: ${chainId}`;
      },
    },
  };
</script>

<style scoped>
  .metamask-connector {
    margin-bottom: 1rem;
  }

  .connection-info {
    display: flex;
    align-items: center;
    background: #f8f9fa;
    border-radius: 6px;
    padding: 0.5rem 1rem;
  }

  .connected-status {
    display: flex;
    align-items: center;
    font-weight: 500;
  }

  .status-dot {
    display: inline-block;
    width: 10px;
    height: 10px;
    background-color: #28a745;
    border-radius: 50%;
  }

  .address-display {
    font-family: monospace;
    font-size: 0.9rem;
  }

  .metamask-button {
    background: rgb(61, 6, 95);
    border-color: rgb(61, 6, 95);
  }
</style>
